let isInstalled = false;

/**
 * Installs monkey-patches on `Node.prototype.removeChild` and `Node.prototype.insertBefore`
 * to protect React DOM reconciliation against browser translation engines (e.g. Google Translate)
 * and third-party extensions that mutate or reparent text nodes and elements behind React's back.
 *
 * Background:
 * Google Translate wraps or replaces text nodes with `<font>` tags. During React reconciliation
 * (especially when unmounting or re-rendering components like preferences cards or chat items),
 * React attempts to insert or remove fiber stateNodes using native DOM methods. If the node
 * was reparented by the translator, native DOM throws fatal unhandled `NotFoundError` exceptions:
 * - "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node."
 * - "Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node."
 *
 * This guard catches those mismatched parent scenarios and either delegates the operation to the
 * actual parent or gracefully no-ops, preventing React error boundaries from crashing the page.
 */
export function installDomTranslationGuard(): void {
  if (typeof window === 'undefined' || typeof Node === 'undefined' || isInstalled) {
    return;
  }

  isInstalled = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn(
          'DOM translation guard: node to remove is not a child of this parent. Delegating removal.',
          child,
          this,
        );
      }
      return child.parentNode ? (child.parentNode.removeChild(child) as T) : child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn(
          'DOM translation guard: reference node is not a child of this parent. Delegating insertion.',
          referenceNode,
          this,
        );
      }
      if (referenceNode.parentNode) {
        return referenceNode.parentNode.insertBefore(newNode, referenceNode) as T;
      }
      return this.appendChild(newNode) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
